/**
 * services/sync/OAuthCloudSyncProvider.ts
 * Abstract base class for OAuth2-based cloud sync providers
 *
 * Provides shared OAuth2 PKCE authentication, token management, and refresh logic
 * for all cloud-based sync providers (Google Drive, Dropbox, OneDrive, Box).
 *
 * Concrete providers extend this class and implement provider-specific API calls.
 */

import { db } from '@/lib/db';
import { supabase } from '@/lib/supabase';
import type {
  SyncProvider,
  SyncProviderType,
  SyncFileInfo,
  SyncError,
  SyncErrorCode,
} from '@/types/sync/SyncProvider';
import type { OAuthConfig, OAuthTokenResponse } from '@/types/sync/OAuthConfig';
import type { OAuthToken } from '@/types/sync/OAuthToken';
import {
  generateCodeVerifier,
  generateCodeChallenge,
  generateState,
  buildAuthorizationUrl,
  openOAuthPopup,
  monitorPopupCallback,
  parseOAuthCallback,
} from '@/utils/oauth';

/**
 * Token refresh buffer time (5 minutes before expiry)
 */
const TOKEN_REFRESH_BUFFER_MS = 5 * 60 * 1000;

/**
 * Abstract OAuth2 cloud sync provider
 *
 * Base class that implements OAuth2 PKCE flow and token management.
 * Concrete providers (GoogleDriveSyncProvider, etc.) extend this and implement
 * provider-specific API calls.
 *
 * **OAuth Flow**:
 * 1. User calls `connect()`
 * 2. Opens popup with authorization URL (PKCE code challenge)
 * 3. User grants permission on provider's site
 * 4. Provider redirects to callback with authorization code
 * 5. Exchange code for access + refresh tokens (PKCE code verifier)
 * 6. Store tokens in IndexedDB
 * 7. Create app folder if needed
 *
 * **Token Management**:
 * - Tokens stored in IndexedDB
 * - Automatic refresh when expired
 * - `ensureValidToken()` checks before all API calls
 *
 * @example
 * ```typescript
 * class GoogleDriveSyncProvider extends OAuthCloudSyncProvider {
 *   readonly name = 'google-drive';
 *
 *   protected getOAuthConfig(): OAuthConfig {
 *     return {
 *       provider: 'google-drive',
 *       clientId: 'YOUR_CLIENT_ID',
 *       authEndpoint: 'https://accounts.google.com/o/oauth2/v2/auth',
 *       tokenEndpoint: 'https://oauth2.googleapis.com/token',
 *       scopes: ['https://www.googleapis.com/auth/drive.file'],
 *       redirectUri: window.location.origin + '/oauth/callback'
 *     };
 *   }
 *
 *   protected async uploadFile(filename: string, content: string): Promise<void> {
 *     // Google Drive API implementation
 *   }
 *   // ... implement other abstract methods
 * }
 * ```
 */
export abstract class OAuthCloudSyncProvider implements SyncProvider {
  /** Provider identifier (set by concrete class) */
  abstract readonly name: SyncProviderType;

  /** Cached access token (loaded from IndexedDB or OAuth flow) */
  protected accessToken: string | null = null;

  /** Cached token expiry timestamp */
  protected tokenExpiry: Date | null = null;

  /** Cached app folder ID (where sync files are stored) */
  protected appFolderId: string | null = null;

  // ===== ABSTRACT METHODS (Provider-Specific) =====

  /**
   * Get OAuth2 configuration for this provider
   *
   * Concrete providers must return their OAuth config with client ID,
   * endpoints, and scopes.
   *
   * @returns OAuth configuration
   */
  protected abstract getOAuthConfig(): OAuthConfig;

  /**
   * Create or locate app-specific folder in cloud storage
   *
   * Each provider has different folder APIs. This method should:
   * 1. Check if app folder exists
   * 2. Create if it doesn't exist
   * 3. Return folder ID
   * 4. Store folder ID in `this.appFolderId`
   *
   * @returns Promise resolving to folder ID
   */
  protected abstract createAppFolder(): Promise<string>;

  /**
   * Upload file to cloud storage using provider's API
   *
   * @param filename - Name of file to upload
   * @param content - File contents as string
   * @returns Promise resolving when upload completes
   */
  protected abstract uploadFile(filename: string, content: string): Promise<void>;

  /**
   * Download file from cloud storage using provider's API
   *
   * @param filename - Name of file to download
   * @returns Promise resolving to file contents as string
   */
  protected abstract downloadFile(filename: string): Promise<string>;

  /**
   * List all sync files in app folder using provider's API
   *
   * @returns Promise resolving to array of file metadata
   */
  protected abstract listFiles(): Promise<SyncFileInfo[]>;

  /**
   * Delete file from cloud storage using provider's API
   *
   * @param filename - Name of file to delete
   * @returns Promise resolving when deletion completes
   */
  protected abstract deleteFile(filename: string): Promise<void>;

  // ===== OAUTH2 FLOW =====

  /**
   * Start OAuth2 PKCE authorization flow
   *
   * Opens popup window for user to grant permissions on provider's site.
   * Waits for callback with authorization code, then exchanges for tokens.
   *
   * @param _options - Optional connection options (provider-specific, used by subclasses)
   * @returns Promise resolving to true if authentication successful
   */
  protected async startOAuthFlow(_options?: import('@/types/sync/SyncProvider').ProviderConnectOptions): Promise<boolean> {
    try {
      const config = this.getOAuthConfig();

      // Generate PKCE parameters
      const codeVerifier = generateCodeVerifier();
      const codeChallenge = await generateCodeChallenge(codeVerifier);
      const state = generateState();

      // Store state in memory (not sessionStorage) since popup flow can't share storage
      // We'll validate it when the callback returns
      const expectedState = state;
      const expectedCodeVerifier = codeVerifier;

      // Build authorization URL
      const authUrl = buildAuthorizationUrl(
        config.authEndpoint,
        config.clientId,
        config.redirectUri,
        config.scopes,
        codeChallenge,
        state,
        config.additionalAuthParams
      );

      // Open popup
      const popup = openOAuthPopup(authUrl);
      if (!popup) {
        console.error('OAuth popup blocked by browser');
        return false;
      }

      console.log('🔐 OAuth popup opened, waiting for user consent...');

      // Wait for callback
      const callbackUrl = await monitorPopupCallback(popup, config.redirectUri);
      console.log('📍 Callback URL received:', callbackUrl);

      const callbackParams = parseOAuthCallback(callbackUrl);
      console.log('📦 Parsed callback params:', callbackParams);

      // Check for errors
      if (callbackParams.error) {
        console.error('OAuth error:', callbackParams.error, callbackParams.errorDescription);
        return false;
      }

      // Verify state (CSRF protection)
      if (callbackParams.state !== expectedState) {
        console.error('OAuth state mismatch - possible CSRF attack');
        console.error('Expected state:', expectedState);
        console.error('Received state:', callbackParams.state);
        return false;
      }

      // Exchange authorization code for tokens
      if (!callbackParams.code) {
        console.error('No authorization code in callback');
        return false;
      }

      const success = await this.exchangeCodeForTokens(callbackParams.code, expectedCodeVerifier);

      if (success) {
        console.log('✅ OAuth authentication successful');
        return true;
      }

      return false;
    } catch (error) {
      console.error('OAuth flow failed:', error);
      return false;
    }
  }

  /**
   * Exchange authorization code for access and refresh tokens
   *
   * Makes POST request to provider's token endpoint with authorization code
   * and PKCE code verifier. Stores received tokens in IndexedDB.
   *
   * @param code - Authorization code from callback
   * @param codeVerifier - PKCE code verifier
   * @returns Promise resolving to true if token exchange successful
   */
  protected async exchangeCodeForTokens(
    code: string,
    codeVerifier: string
  ): Promise<boolean> {
    try {
      const config = this.getOAuthConfig();

      // Build token request
      const tokenParams = new URLSearchParams({
        grant_type: 'authorization_code',
        code,
        code_verifier: codeVerifier,
        client_id: config.clientId,
        redirect_uri: config.redirectUri,
        ...config.additionalTokenParams,
      });

      // Make token request
      const response = await fetch(config.tokenEndpoint, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/x-www-form-urlencoded',
        },
        body: tokenParams.toString(),
      });

      if (!response.ok) {
        const errorText = await response.text();
        console.error('Token exchange failed:', response.status, errorText);
        return false;
      }

      const tokenResponse: OAuthTokenResponse = await response.json();

      // Store tokens
      await this.storeTokens(
        tokenResponse.access_token,
        tokenResponse.refresh_token || null,
        tokenResponse.expires_in
      );

      return true;
    } catch (error) {
      console.error('Error exchanging code for tokens:', error);
      return false;
    }
  }

  /**
   * Refresh expired access token using refresh token
   *
   * Makes POST request to token endpoint with refresh token to obtain
   * a new access token. Updates stored tokens in IndexedDB.
   *
   * @returns Promise resolving to true if refresh successful
   */
  protected async refreshAccessToken(): Promise<boolean> {
    try {
      // Load current token from IndexedDB
      let storedToken;
      try {
        storedToken = await db.oauth_tokens.get(this.name);
      } catch (dbError) {
        if (dbError instanceof Error) {
          if (dbError.name === 'InvalidStateError') {
            console.error(`❌ Failed to read tokens for ${this.name}: Database is closed or corrupted.`, dbError);
          } else {
            console.error(`❌ Failed to read tokens for ${this.name}:`, dbError);
          }
        }
        return false;
      }

      if (!storedToken || !storedToken.refreshToken) {
        console.error('No refresh token available');
        return false;
      }

      const config = this.getOAuthConfig();

      // Build refresh request
      const refreshParams = new URLSearchParams({
        grant_type: 'refresh_token',
        refresh_token: storedToken.refreshToken,
        client_id: config.clientId,
        ...config.additionalTokenParams,
      });

      // Make refresh request
      const response = await fetch(config.tokenEndpoint, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/x-www-form-urlencoded',
        },
        body: refreshParams.toString(),
      });

      if (!response.ok) {
        const errorText = await response.text();
        console.error('Token refresh failed:', response.status, errorText);

        // If refresh fails with 401/403, clear tokens (user needs to re-authenticate)
        if (response.status === 401 || response.status === 403) {
          await this.clearTokens();
        }

        return false;
      }

      const tokenResponse: OAuthTokenResponse = await response.json();

      // Store new tokens (some providers return new refresh token, some don't)
      await this.storeTokens(
        tokenResponse.access_token,
        tokenResponse.refresh_token || storedToken.refreshToken,
        tokenResponse.expires_in
      );

      console.log('✅ Access token refreshed successfully');
      return true;
    } catch (error) {
      console.error('Error refreshing access token:', error);
      return false;
    }
  }

  /**
   * Ensure access token is valid (not expired)
   *
   * Checks if token is expired or will expire soon, and refreshes if needed.
   * Call this before every API operation to ensure valid authentication.
   *
   * @returns Promise resolving when token is valid
   * @throws Error if token is invalid and cannot be refreshed
   */
  protected async ensureValidToken(): Promise<void> {
    // Load token from IndexedDB if not cached
    if (!this.accessToken || !this.tokenExpiry) {
      let storedToken;
      try {
        storedToken = await db.oauth_tokens.get(this.name);
      } catch (dbError) {
        // Database error - treat as critical since all API operations depend on this
        if (dbError instanceof Error) {
          if (dbError.name === 'InvalidStateError') {
            throw this.createSyncError(
              dbError,
              'UNKNOWN_ERROR',
              'Failed to read authentication tokens: Database is closed or corrupted.'
            );
          } else {
            throw this.createSyncError(
              dbError,
              'UNKNOWN_ERROR',
              'Failed to read authentication tokens from storage.'
            );
          }
        }
        throw this.createSyncError(
          dbError,
          'UNKNOWN_ERROR',
          'Failed to read authentication tokens from storage.'
        );
      }

      if (!storedToken) {
        throw this.createSyncError(
          new Error('Not authenticated'),
          'NOT_CONNECTED',
          'No access token found. Call connect() first.'
        );
      }

      this.accessToken = storedToken.accessToken;
      this.tokenExpiry = new Date(storedToken.expiresAt);
    }

    // Check if token is expired or will expire soon
    const now = new Date();
    const expiresIn = this.tokenExpiry.getTime() - now.getTime();

    if (expiresIn < TOKEN_REFRESH_BUFFER_MS) {
      console.log('🔄 Access token expired or expiring soon, refreshing...');

      const refreshed = await this.refreshAccessToken();
      if (!refreshed) {
        // Clear cached token and throw error
        this.accessToken = null;
        this.tokenExpiry = null;

        throw this.createSyncError(
          new Error('Token refresh failed'),
          'PERMISSION_DENIED',
          'Access token expired and refresh failed. Call connect() to re-authenticate.'
        );
      }
    }
  }

  /**
   * Store OAuth tokens in IndexedDB
   *
   * @param accessToken - Access token
   * @param refreshToken - Refresh token (may be null for some providers)
   * @param expiresIn - Token expiration in seconds
   */
  protected async storeTokens(
    accessToken: string,
    refreshToken: string | null,
    expiresIn: number
  ): Promise<void> {
    const now = new Date();
    const expiresAt = new Date(now.getTime() + expiresIn * 1000);

    const token: OAuthToken = {
      id: this.name,
      provider: this.name,
      accessToken,
      refreshToken,
      expiresAt: expiresAt.toISOString(),
      createdAt: now.toISOString(),
      updatedAt: now.toISOString(),
    };

    try {
      await db.oauth_tokens.put(token);

      // Update cached values
      this.accessToken = accessToken;
      this.tokenExpiry = expiresAt;

      console.log(`✅ Tokens stored for ${this.name}`);
    } catch (error) {
      // Detect specific error types
      if (error instanceof Error) {
        if (error.name === 'QuotaExceededError') {
          console.error(`❌ Failed to store tokens for ${this.name}: Storage quota exceeded. Please free up space.`, error);
        } else if (error.name === 'InvalidStateError') {
          console.error(`❌ Failed to store tokens for ${this.name}: Database is closed or corrupted.`, error);
        } else {
          console.error(`❌ Failed to store tokens for ${this.name}:`, error);
        }
      } else {
        console.error(`❌ Failed to store tokens for ${this.name}:`, error);
      }

      // Re-throw - caller needs to know token storage failed
      throw error;
    }
  }

  /**
   * Clear OAuth tokens via BFF disconnect operation
   *
   * Calls the BFF disconnect endpoint which:
   * - Revokes the access token at the provider (Google, OneDrive, etc.)
   * - Clears tokens from server-side database (soft delete with audit trail)
   * - Clears folder_id (new account will use different folder)
   * - Keeps provider_user_id and provider_email for audit/UX
   */
  protected async clearTokens(): Promise<void> {
    try {
      // Call BFF disconnect operation
      const { data, error } = await supabase.functions.invoke('google-drive-proxy', {
        body: { operation: 'disconnect' }
      });

      if (error) {
        console.error(`⚠️ BFF disconnect error for ${this.name}:`, error);
        throw error;
      }

      if (!data?.ok) {
        const errorMsg = data?.message || 'Unknown error';
        console.error(`⚠️ BFF disconnect failed for ${this.name}:`, errorMsg);
        throw new Error(errorMsg);
      }

      console.log(`✅ Tokens cleared for ${this.name} (revoked at provider)`);
    } catch (error) {
      // Log but don't throw - clearing tokens is cleanup, shouldn't block disconnect
      console.error(`⚠️ Failed to disconnect from ${this.name} via BFF:`, error);
    }

    // Always clear cached values regardless of BFF operation success
    this.accessToken = null;
    this.tokenExpiry = null;
    this.appFolderId = null;
  }

  // ===== SYNCPROVIDER INTERFACE IMPLEMENTATION =====

  /**
   * Connect to cloud provider via OAuth2
   *
   * Initiates OAuth2 PKCE flow, obtains tokens, and creates app folder.
   * User will see a popup to grant permissions.
   *
   * @param options - Optional connection options (passed to startOAuthFlow)
   * @returns Promise resolving to true if connection successful
   */
  async connect(options?: import('@/types/sync/SyncProvider').ProviderConnectOptions): Promise<boolean> {
    try {
      console.log(`🔗 Connecting to ${this.name}...`);

      // Check if already connected
      const alreadyConnected = await this.isConnected();

      if (!alreadyConnected) {
        // Start OAuth flow (only if not already connected)
        const authenticated = await this.startOAuthFlow(options);
        if (!authenticated) {
          console.error('OAuth authentication failed');
          return false;
        }

        // Create app folder
        console.log('📁 Creating app folder...');
        this.appFolderId = await this.createAppFolder();
      } else {
        console.log(`✅ Already connected to ${this.name} (tokens cached)`);
        // Close pre-opened popup if we're already connected (no OAuth flow needed)
        if (options?.oauthPopup && !options.oauthPopup.closed) {
          options.oauthPopup.close();
        }
      }

      // Clear disconnected_at via BFF to trigger realtime events (Task 2.9)
      // Call this ALWAYS (even if already connected) because:
      // - If we were disconnected, this clears the flag and fires realtime to other tabs
      // - If we weren't disconnected, this is idempotent (NULL → NULL is safe)
      try {
        const { data, error } = await supabase.functions.invoke('google-drive-proxy', {
          body: { operation: 'reconnect' }
        });

        if (error || !data?.ok) {
          console.warn(`⚠️ Failed to clear disconnected_at (non-critical):`, error || data);
          // Non-critical - connection still works, just no realtime notification to other tabs
        } else {
          console.log(`✅ Reconnect status cleared for ${this.name}`);
        }
      } catch (reconnectError) {
        console.warn(`⚠️ Failed to call reconnect endpoint (non-critical):`, reconnectError);
        // Non-critical - fail soft, connection still works locally
      }

      console.log(`✅ Successfully connected to ${this.name}`);
      return true;
    } catch (error) {
      // Rethrow errors that need targeted UI handling so hook can display appropriate messaging
      if (error instanceof Error && (
        error.message === 'USER_CANCELLED' ||
        error.message === 'ACCESS_DENIED' ||
        error.message === 'INSUFFICIENT_SCOPES' ||
        error.message === 'PROVIDER_OUTAGE'
      )) {
        throw error;
      }
      console.error(`Failed to connect to ${this.name}:`, error);
      return false;
    }
  }

  /**
   * Check if provider is currently authenticated
   *
   * Verifies that valid access token exists (not expired).
   *
   * @returns Promise resolving to true if connected
   */
  async isConnected(): Promise<boolean> {
    try {
      // Try to load token from IndexedDB
      const storedToken = await db.oauth_tokens.get(this.name);
      if (!storedToken) {
        return false;
      }

      // Check if token is expired
      const expiresAt = new Date(storedToken.expiresAt);
      const now = new Date();

      // Consider connected if token exists and hasn't expired
      // (refresh will happen automatically on next API call)
      return expiresAt.getTime() > now.getTime();
    } catch (error) {
      // Log database errors but return false (not connected)
      if (error instanceof Error) {
        if (error.name === 'InvalidStateError') {
          console.error(`⚠️ Failed to check connection for ${this.name}: Database is closed or corrupted.`, error);
        } else if (error.name !== 'NotFoundError') {
          // NotFoundError is expected (no token = not connected), don't log
          console.error(`⚠️ Failed to check connection for ${this.name}:`, error);
        }
      }
      return false;
    }
  }

  /**
   * Read file contents from cloud storage
   *
   * @param filename - Name of file to read
   * @returns Promise resolving to file contents as string
   * @throws SyncError if not connected, file not found, or permission denied
   */
  async read(filename: string): Promise<string> {
    await this.ensureValidToken();
    return await this.downloadFile(filename);
  }

  /**
   * Write file contents to cloud storage
   *
   * Creates new file if it doesn't exist, overwrites if it does.
   *
   * @param filename - Name of file to write
   * @param data - File contents as string
   * @returns Promise resolving when write completes
   * @throws SyncError if not connected, permission denied, or quota exceeded
   */
  async write(filename: string, data: string): Promise<void> {
    await this.ensureValidToken();
    await this.uploadFile(filename, data);
  }

  /**
   * List all sync files in cloud storage
   *
   * @returns Promise resolving to array of file metadata
   * @throws SyncError if not connected or permission denied
   */
  async list(): Promise<SyncFileInfo[]> {
    await this.ensureValidToken();
    return await this.listFiles();
  }

  /**
   * Delete file from cloud storage
   *
   * @param filename - Name of file to delete
   * @returns Promise resolving when deletion completes
   * @throws SyncError if not connected, file not found, or permission denied
   */
  async delete(filename: string): Promise<void> {
    await this.ensureValidToken();
    await this.deleteFile(filename);
  }

  /**
   * Disconnect from provider and cleanup resources
   *
   * Clears OAuth tokens from IndexedDB. User will need to re-authenticate
   * to reconnect.
   *
   * @returns Promise resolving when disconnect completes
   */
  async disconnect(): Promise<void> {
    await this.clearTokens();
    console.log(`✅ Disconnected from ${this.name}`);
  }

  /**
   * Get unique identifier for cloud storage location
   *
   * For cloud providers, returns the app folder ID as the directory identifier.
   * This enables proper conflict detection when switching between accounts.
   *
   * @returns Promise resolving to app folder ID or null if not connected
   */
  async getDirectoryId(): Promise<string | null> {
    try {
      // Return cached folder ID if available
      if (this.appFolderId) {
        return this.appFolderId;
      }

      // Check if connected
      if (!(await this.isConnected())) {
        return null;
      }

      // Ensure valid token and create/get app folder
      await this.ensureValidToken();
      this.appFolderId = await this.createAppFolder();

      return this.appFolderId;
    } catch (error) {
      console.error('Failed to get directory ID:', error);
      return null;
    }
  }

  // ===== HELPER METHODS =====

  /**
   * Create structured SyncError from native error
   *
   * Maps HTTP status codes and error types to SyncErrorCode for consistent
   * error handling across all providers.
   *
   * @param error - Native error from API call
   * @param fallbackCode - Default error code if mapping fails
   * @param message - Human-readable error message
   * @returns Structured SyncError
   */
  protected createSyncError(
    error: unknown,
    fallbackCode: SyncErrorCode,
    message: string
  ): SyncError {
    let code: SyncErrorCode = fallbackCode;

    // Map HTTP status codes to sync error codes
    if (error instanceof Response) {
      switch (error.status) {
        case 401:
        case 403:
          code = 'PERMISSION_DENIED';
          break;
        case 404:
          code = 'FILE_NOT_FOUND';
          break;
        case 429:
        case 503:
          code = 'NETWORK_ERROR';
          break;
        case 507:
          code = 'QUOTA_EXCEEDED';
          break;
        default:
          code = fallbackCode;
      }
    }

    // Map error names to sync error codes
    if (error instanceof Error) {
      if (error.message.includes('quota') || error.message.includes('storage')) {
        code = 'QUOTA_EXCEEDED';
      } else if (error.message.includes('network') || error.message.includes('timeout')) {
        code = 'NETWORK_ERROR';
      } else if (error.message.includes('permission') || error.message.includes('auth')) {
        code = 'PERMISSION_DENIED';
      } else if (error.message.includes('not found')) {
        code = 'FILE_NOT_FOUND';
      }
    }

    return {
      code,
      message,
      provider: this.name,
      details: error,
    };
  }

  /**
   * Make authenticated HTTP request to provider API
   *
   * Helper method that adds Authorization header with access token.
   *
   * @param url - API endpoint URL
   * @param options - Fetch options
   * @returns Promise resolving to Response
   */
  protected async makeAuthenticatedRequest(
    url: string,
    options: { method?: string; body?: string; headers?: Record<string, string> } = {}
  ): Promise<Response> {
    if (!this.accessToken) {
      throw this.createSyncError(
        new Error('No access token'),
        'NOT_CONNECTED',
        'Not authenticated. Call connect() first.'
      );
    }

    const headers = {
      ...options.headers,
      Authorization: `Bearer ${this.accessToken}`,
    };

    return await fetch(url, {
      ...options,
      headers,
    });
  }
}
